import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import { format, parseISO } from 'date-fns';
import type {
  BarSeriesOption,
  GridComponentOption,
  LegendComponentOption,
  TitleComponentOption,
  TooltipComponentOption,
} from 'echarts';

import type { HeroPaymentCycleFunnelBucket } from './heroPaymentCycleFunnelApi';

type ECOption = echarts.ComposeOption<
  | BarSeriesOption
  | TitleComponentOption
  | TooltipComponentOption
  | LegendComponentOption
  | GridComponentOption
>;

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  LegendComponent,
  LabelLayout,
  UniversalTransition,
  SVGRenderer,
]);

const formatPeriodLabel = (date: Date, period: string, fallback: string) => {
  switch (period) {
    case 'daily':
      return format(date, 'MMM d');
    case 'weekly':
      return `W${format(date, 'II')} ${format(date, 'yyyy')}`;
    case 'monthly':
      return format(date, 'MMM yyyy');
    case 'quarterly':
      return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
    default:
      return fallback;
  }
};

const HeroPaymentCycleFunnelTrendChart: React.FC<{
  data: HeroPaymentCycleFunnelBucket[];
  period: string;
}> = ({ data, period }) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const chartInstance = React.useRef<echarts.ECharts | null>(null);

  React.useEffect(() => {
    if (!chartRef.current) return undefined;
    if (!data?.length) {
      chartInstance.current?.clear();
      return undefined;
    }

    const labels = data.map((row) => {
      try {
        return formatPeriodLabel(parseISO(row.period_start), period, row.period);
      } catch {
        return row.period;
      }
    });

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const option: ECOption = {
      tooltip: { trigger: 'axis' },
      legend: { data: ['WhatsApp sent', 'Link opened', 'Payment term chosen'] },
      grid: { left: 48, right: 24, bottom: 48, top: 48 },
      xAxis: { type: 'category', data: labels },
      yAxis: { type: 'value', minInterval: 1 },
      series: [
        {
          name: 'WhatsApp sent',
          type: 'bar',
          data: data.map((r) => r.whatsapp_sent),
        },
        {
          name: 'Link opened',
          type: 'bar',
          data: data.map((r) => r.link_opened),
        },
        {
          name: 'Payment term chosen',
          type: 'bar',
          data: data.map((r) => r.payment_term_selected),
        },
      ],
    };

    chartInstance.current.setOption(option);
    const onResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [data, period]);

  return <div ref={chartRef} style={{ width: '100%', height: 320 }} />;
};

export default HeroPaymentCycleFunnelTrendChart;
